import { ResultOf } from 'gql.tada';
import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';

import {
  ComboboxOption,
  Icon,
  LoadingIcon,
  Stack,
  useComboboxInputValue,
  useSetComboboxInputValue,
} from '@eluve/components';

import { NewPatientOption } from './NewPatientOption';
import { PatientOption } from './PatientOption';
import { patientOptionFragment } from './operations';

export interface PatientComboboxContentProps {
  selectedPatient: ResultOf<typeof patientOptionFragment> | null;
  searchResults: ResultOf<typeof patientOptionFragment>[];
  loading: boolean;
  handlePatientSelected: (patientId?: string) => void;
  tenantId: string;
  debouncedSearch: ReturnType<typeof debounce>;
  setSearchResults: React.Dispatch<
    React.SetStateAction<ResultOf<typeof patientOptionFragment>[]>
  >;
}

export const PatientComboboxContent: React.FC<PatientComboboxContentProps> = ({
  selectedPatient,
  searchResults,
  loading,
  handlePatientSelected,
  tenantId,
  debouncedSearch,
  setSearchResults,
}) => {
  const inputValue = useComboboxInputValue();
  const setInputValue = useSetComboboxInputValue();

  const prevInputRef = useRef(inputValue);

  useEffect(
    function searchOnInputChange() {
      if (prevInputRef.current !== inputValue) {
        prevInputRef.current = inputValue;

        if (!inputValue || inputValue.length < 2) {
          setSearchResults([]);
        } else {
          debouncedSearch(inputValue);
        }
      }
    },
    [inputValue, debouncedSearch, setSearchResults],
  );

  useEffect(
    function clearResultsOnMount() {
      setSearchResults([]);
    },
    [setSearchResults],
  );

  return (
    <>
      {selectedPatient?.id && (
        <button
          className="mb-2 w-full rounded-md px-4 py-2 text-left hover:bg-bg-quaternary"
          onClick={() => {
            handlePatientSelected(undefined);
            setInputValue('');
          }}
        >
          <Stack
            className="text-text-tertiary"
            direction="horizontal"
            fullWidth={false}
            gap={2}
          >
            <Icon name="X" size="xl" className="m-1" />
            <span>Clear Selected Patient</span>
          </Stack>
        </button>
      )}

      {loading && (
        <Stack
          className="py-4"
          fullWidth={false}
          gap={2}
          align="center"
          justify="center"
        >
          <LoadingIcon size="xl" className="text-bg-accent-strong-primary" />
          <span className="mt-2 text-text-secondary">
            Searching patients...
          </span>
        </Stack>
      )}

      {inputValue.length >= 2 || searchResults.length === 0 ? null : (
        <div className="px-4 py-2 text-text-tertiary">
          Type at least 2 characters to search
        </div>
      )}

      {searchResults.map((patient) => {
        return (
          <ComboboxOption
            key={patient.id}
            onSelect={() => {
              handlePatientSelected(patient.id);
              setInputValue('');
              setSearchResults([]);
            }}
          >
            <Stack direction="horizontal" gap={2}>
              <div className="flex-1">
                <PatientOption {...patient} />
              </div>
              {selectedPatient?.id === patient.id && (
                <Icon
                  name="Check"
                  size="xl"
                  className="w-max text-bg-accent-strong-primary"
                />
              )}
            </Stack>
          </ComboboxOption>
        );
      })}

      {inputValue.length >= 2 && (
        <NewPatientOption
          tenantId={tenantId}
          onCreateNewPatient={(patientId) => {
            handlePatientSelected(patientId);
            setInputValue('');
          }}
        />
      )}
    </>
  );
};
